/* ==========================================================================
   Dynoforce kentekenflow
   20-10-2026 (ronde 3, spoor thema, punt 6): Engels "licence plate" (Brits,
   EU-markt) in plaats van "license plate" in alle zichtbare teksten.
   Formatteert het kenteken tijdens het typen, draait de scananimatie van
   ongeveer 1,1 seconde en vult daarna de resultaatkaart van de sectie.
   Data komt uit window.DFData; zie df-data.js voor de echte-API-omschakeling.

   Contract per sectie (zie ook snippets/df-kenteken-plaat.liquid):
   - form[data-df-kt] met input[data-df-kt-input] en [data-df-kt-status]
   - in dezelfde sectie een [data-df-kt-result] die start met [hidden]
   - tekstslots: [data-kt="..."] -> textContent
   - balken: [data-kt-bar="pk|nm"] -> breedte in procenten (origineel als
     aandeel van Stage 1), met een CSS-transition op width in de sectie
   ========================================================================== */

(function () {
  'use strict';

  /* De dienst schrijft de teksten, niet het thema. Alleen daar is bekend welk
     van de gevallen het is, en het verschil is commercieel: "we kennen je auto
     niet" jaagt iemand weg, "we kunnen je auto wel, we missen alleen het
     getal" levert een telefoontje op. Elk antwoord met gevonden:false stuurt
     sinds platformversie 3.161.0 een tekst mee in het veld fout.

     Let op de valkuil die ons hier eerder beet: niet_gevonden betekent aan die
     kant "auto is bekend, nog geen tuning in de lijst". Een kenteken dat niet
     bestaat heet kenteken_onbekend. Twee verschillende dingen, en het thema
     kan ze niet uit elkaar houden - dus moet het ze ook niet zelf willen
     verwoorden.

     Hieronder staat puur een vangnet voor een antwoord zonder fout-tekst.
     Platte tekst, geen HTML: de telefoon- en WhatsApp-link worden er los
     achter gezet, zodat er nooit opmaak uit een antwoord de pagina in kan. */
  var MELDINGEN = {
    kan_wel:           'Deze auto kunnen we tunen, alleen staan de cijfers er nog niet bij.',
    meerdere:          'Van deze auto bestaan meerdere uitvoeringen.',
    niet_gevonden:     'We kennen deze auto, maar er staat nog geen tuning voor in onze lijst.',
    geen_gegevens:     'We hebben te weinig gegevens van deze auto om mee te rekenen.',
    kenteken_onbekend: 'Dit kenteken staat niet in het register. Klopt het zo?',
    limiet:            'Je hebt net veel kentekens opgezocht. Probeer het over een paar minuten nog eens.',
    standaard:         'We kunnen hier nu geen cijfers bij vinden.'
  };

  /* Engels (11-10-2026). Op /en-eu/ stonden de meldingen, de statusregel en
     de keuzelijst in het Nederlands. De dienst schrijft zijn teksten in het
     Nederlands, dus op een Engelse pagina nemen we niet zijn fout-tekst
     maar de Engelse tegenhanger hieronder, op basis van de status. Die
     zinnen volgen de Nederlandse teksten van de dienst
     (src/app/api/kentekencheck/route.ts in het platform). Op een
     Nederlandse pagina verandert er niets. */
  var EN = /^en/i.test(document.documentElement.lang || '');
  var MELDINGEN_EN = {
    meerdere:          'There are several versions of this car. Choose yours below.',
    elektrisch:        'This car is fully electric, so there is nothing to gain with ECU tuning. Feel free to call or message us if you want to know what is possible.',
    kan_wel:           "We can tune this car, the figures just aren't in our list yet. Call or message us and we'll look it up for you.",
    niet_gevonden:     "There is no tune for this car in our list yet. Call or message us and we'll look at what's possible together.",
    geen_gegevens:     "We couldn't retrieve enough data for this licence plate to calculate with. Call or message us.",
    kenteken_onbekend: "This licence plate isn't in the vehicle register. Is it correct?",
    limiet:            "You've just looked up quite a few licence plates. Please try again in a few minutes.",
    standaard:         "We can't find any figures for this right now."
  };
  /* Foutteksten uit de datalaag (df-data.js), die alleen Nederlands kent. */
  var FOUTEN_EN = {
    'Vul een volledig kenteken in.': 'Enter a complete licence plate.',
    'Het duurt langer dan normaal. Probeer het nog een keer, of bel 0525-796515.': 'This is taking longer than usual. Try again, or call 0525-796515.',
    'De kentekenservice reageert niet. Probeer het zo nog een keer.': 'The licence plate service is not responding. Please try again shortly.'
  };
  function T(nl, en) { return EN ? en : nl; }

  /* De melding bij een antwoord zonder cijfers, in de taal van de pagina. */
  function meldingTekst(v, reden) {
    if (!EN) return (v && v.fout) || MELDINGEN[reden] || MELDINGEN.standaard;
    if (v && String(v.brandstof || '').toLowerCase() === 'elektrisch' &&
        reden !== 'limiet' && reden !== 'kenteken_onbekend') return MELDINGEN_EN.elektrisch;
    return MELDINGEN_EN[reden] || MELDINGEN_EN.standaard;
  }
  function foutTekst(err) {
    var m = err && err.message ? err.message : '';
    if (!EN) return m;
    return FOUTEN_EN[m] || m || MELDINGEN_EN.standaard;
  }

  var reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Eenheid van het vermogen (11-10-2026). Het platform zegt pk; op de
     Engelse pagina's (lang en) heet dat hp. Nm blijft overal Nm. */
  function pkEenheid(e) {
    var eh = e || 'pk';
    return /^en/i.test(document.documentElement.lang || '') && /^pk$/i.test(eh) ? 'hp' : eh;
  }

  /* Ook in de naam van de motorisering staat pk ("2.0 TSI GTI 230pk"). Op de
     Engelse pagina's wordt een getal met pk erachter een getal met hp; in het
     Nederlands verandert er niets (11-10-2026). Alleen voor wat op het scherm
     staat: wat naar ons gaat (het formulier, het WhatsApp-bericht) houdt de
     naam zoals het platform hem geeft. */
  function pkInTekst(t) {
    return String(t || '').replace(/(\d)(\s?)(pk)\b/gi, function (m, cijfer, spatie, eh) {
      return cijfer + spatie + pkEenheid(eh);
    });
  }

  /* Meetpunt (MEET-2, 10-10-2026). Na elke uitkomst van een opvraging roepen
     we df:kentekencheck om; df-leads.js geeft dat door aan de meting. Alleen
     de uitkomst en het pad, nooit het kenteken. Een eigen naam, want
     df:kenteken is al van de datalaag (df-data.js) met {kt, v} erin. */
  function meld(uitkomst) {
    try {
      document.dispatchEvent(new CustomEvent('df:kentekencheck', {
        detail: { uitkomst: uitkomst, pagina: location.pathname }
      }));
    } catch (e) {}
  }

  /* Waar de knop "Passende onderdelen" heen gaat (10-10-2026). Die wees naar
     de hele shop met het kenteken erachter, en daar leest niets dat kenteken
     uit: de bezoeker kreeg alle 2200 producten. De autokiezer in de menubalk
     (df-auto.js) weet al welke modelcollectie bij een antwoord hoort en geeft
     die herkenning door via window.DFAuto. Geen modelcollectie? Dan de
     alle-collectie van het merk, en pas als ook het merk onbekend is de shop.

     Alleen alle-collecties die er echt zijn. De autokiezer kent twintig
     merken, maar zo'n verzamelpagina bestaat voor vijf; voor een Porsche is
     de shop de eerlijkste terugval, een link naar een pagina die niet
     bestaat niet. */
  var MERK_COLLECTIES = {
    bmw: 'alle-bmw', audi: 'alle-audi', volkswagen: 'alle-volkswagen', vw: 'alle-volkswagen',
    mercedesbenz: 'alle-mercedes-benz', mercedes: 'alle-mercedes-benz', mercedesamg: 'alle-mercedes-benz',
    mini: 'alle-mini'
  };

  function onderdelenPad(v) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var res = null;
    try {
      if (window.DFAuto && window.DFAuto.herken) res = window.DFAuto.herken(v);
    } catch (e) { res = null; }
    if (res && res.niveau === 'zeker' && res.item && res.item.u) return res.item.u;
    var merk = String((res && res.merk) || (v && v.merk) || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
    if (MERK_COLLECTIES[merk]) return root + 'collections/' + MERK_COLLECTIES[merk];
    return root + 'collections/shop';
  }

  /* Sitebreed onthouden. De laatste geslaagde opvraging staat in localStorage
     onder df_kenteken: het nette kenteken plus het volledige zeker-antwoord.
     Daarmee vult elke pagina het veld voor in en zet de rekenkaart direct
     neer, uit het geheugen en dus zonder nieuwe opvraging. Na dertig dagen
     telt hij niet meer mee. Wissen gebeurt met de wisknop in het
     autokiezer-paneel; die zit in df-auto.js en wist beide sleutels. */
  var KT_SLEUTEL = 'df_kenteken';
  var KT_HOUDBAAR = 30 * 24 * 60 * 60 * 1000;

  function ktLees() {
    /* De datalaag houdt dit bij en kan terugvallen op sessionStorage als de
       browser localStorage weigert; daarom eerst daar vragen. */
    if (window.DFData && window.DFData.onthoudenKenteken) {
      return window.DFData.onthoudenKenteken();
    }
    try {
      var r = JSON.parse(localStorage.getItem(KT_SLEUTEL) || 'null');
      if (!r || !r.kt || !r.ts || Date.now() - r.ts > KT_HOUDBAAR) return null;
      return r;
    } catch (e) { return null; }
  }
  function ktBewaar(kt, v) {
    /* Schrijven loopt via de datalaag, want die bewaakt ook de terugval op
       sessionStorage en roept de wijziging om aan de rest van de pagina. */
    if (window.DFData && window.DFData.onthoudKenteken) {
      window.DFData.onthoudKenteken(kt, v);
      return;
    }
    try {
      localStorage.setItem(KT_SLEUTEL, JSON.stringify({ kt: kt, v: v || null, ts: Date.now() }));
    } catch (e) {}
  }

  /* Handmatige keuze naast een onthouden kenteken (TUN-1, 17-10-2026). De
     tunekiezer (df-tunekiezer.js) kent geen kenteken, maar in de plaat staat
     dan vaak nog het kenteken van een eerdere zoekactie. Alleen als dat
     kenteken en de keuze dezelfde auto zijn mag de keuze eraan hangen. Merk
     en model worden plat vergeleken (kleine letters, alleen letters en
     cijfers), met dezelfde merkaliassen als de autokiezer in het menu
     (df-auto.js). Bij twijfel geen koppeling: liever een kaart zonder
     kenteken dan een kenteken bij de verkeerde auto. */
  var MERK_ALIAS = { vw: 'volkswagen', mercedes: 'mercedesbenz', mercedesamg: 'mercedesbenz' };
  function plat(s) { return String(s == null ? '' : s).toLowerCase().replace(/[^a-z0-9]+/g, ''); }
  function merkSleutel(s) { var k = plat(s); return MERK_ALIAS[k] || k; }
  function zelfdeAuto(a, b) {
    if (!a || !b) return false;
    return !!plat(b.model) && merkSleutel(a.merk) === merkSleutel(b.merk) && plat(a.model) === plat(b.model);
  }

  function init(root) {
    root.querySelectorAll('form[data-df-kt]').forEach(function (form) {
      if (form.dataset.dfKtInit) return;
      form.dataset.dfKtInit = '1';

      var sectie = form.closest('.shopify-section') || document;
      var input = form.querySelector('[data-df-kt-input]');
      var plaat = form.querySelector('.df-kt');
      var status = form.querySelector('[data-df-kt-status]');
      var result = sectie.querySelector('[data-df-kt-result]');
      if (!input || !window.DFData) return;

      /* Haakje voor de handmatige tunekiezer (df-tunekiezer.js). Die kent merk,
         model en uitvoering maar geen kenteken, en hoort exact dezelfde
         resultaatkaart en keuzelijst te krijgen als de kentekenroute - anders
         staan er twee kaarten met elk hun eigen rekenwerk. Functiedeclaraties
         hoisten, dus deze verwijzingen mogen hier al staan.

         kies(v, u) neemt de gekozen rij over; keuze(v, kop) tekent de lijst. */
      form.dfKt = { kies: neem, keuze: kiesUitvoering };

      input.addEventListener('input', function () {
        input.value = window.DFData.formatPlate(input.value);
      });

      var knop = form.querySelector('button[type="submit"], [type="submit"]');
      var knopTekst = knop ? knop.textContent : '';

      /* Schuift iets in beeld, maar alleen als het (deels) buiten het scherm
         staat; wat al in beeld is blijft waar het is, anders springt de
         pagina bij elke opvraging. Een kort blok komt in het midden; een
         blok dat meer dan de helft van het scherm vult komt met zijn
         bovenkant bovenin, zodat de kop leesbaar blijft. Op een telefoon
         stond de melding of keuzelijst na het zoeken anders boven het
         scherm, met een leeg kader in beeld. */
      function inBeeld(el) {
        if (!el) return;
        var k = el.getBoundingClientRect();
        var h = window.innerHeight;
        if (k.top >= 0 && k.bottom <= h) return;
        el.scrollIntoView({
          behavior: reduced ? 'auto' : 'smooth',
          block: k.height > h * 0.5 ? 'start' : 'center'
        });
      }

      /* De foto en het bijschrift horen bij de auto die in de kaart staat. Bij
         een nieuwe zoekactie moeten ze dus mee weg, anders staat de vorige auto
         nog in beeld naast de cijfers van de volgende - of naast een melding
         dat er geen cijfers zijn. */
      function wagenWeg() {
        if (!result) return;
        var w = result.parentElement ? result.parentElement.querySelector('[data-kt-wagen]') : null;
        if (!w) w = document.querySelector('[data-kt-wagen]');
        var blok = w && (w.closest('.df-kh__wagenblok') || w);
        if (blok) blok.hidden = true;
        if (w) w.removeAttribute('src');
        var bij = document.querySelector('[data-kt-bijschrift]');
        if (bij) bij.hidden = true;
      }

      /* Een opvraging duurt ongeveer twee seconden, en de eerste keer dat de
         dienst een auto ziet ruim drie. Twee seconden niets is lang; twee
         seconden met beweging niet. Daarom komt de kaart meteen in beeld, met
         balken op de plek van de cijfers. */
      function skelet(aan) {
        if (!result) return;
        if (aan) {
          result.hidden = false;
          result.classList.add('laden');
          /* Hier stond een scroll naar de kaart, zodat de laadbalken in beeld
             kwamen. Dat pakte verkeerd uit: de sectie tekent de kaart pas bij
             de klasse on, dus de bezoeker keek seconden naar een leeg vlak,
             en bleef het bij een melding of keuzelijst, dan stond die boven
             het scherm terwijl het lege kader in beeld bleef. Nu blijft de
             pagina staan waar de bezoeker is; alleen de statusregel onder de
             plaat ("We zoeken je auto op") moet in beeld zijn. De kaart komt
             in beeld zodra vul() hem gevuld heeft, en de secties geven de
             klasse laden sinds 10-10-2026 een zichtbare laadstand. */
          inBeeld(status);
          result.querySelectorAll('[data-kt]').forEach(function (el) {
            el.textContent = ' ';
          });
          result.querySelectorAll('[data-kt-bar]').forEach(function (el) {
            el.style.width = '0%';
          });
        } else {
          result.classList.remove('laden');
        }
      }

      var bezig = false;
      /* Het kenteken dat nu in de kaart staat. Zie de luisteraar op
         df:kenteken hieronder. */
      var getekendKt = '';
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        if (bezig) return;
        var kt = input.value.trim();
        if (!kt) {
          /* Een lege plaat kreeg alleen de cursor terug, zonder een woord
             uitleg; dat leest als een knop die niets doet. Op /en/ in het
             Engels; de taal staat op het html-element. */
          var engels = /^en/i.test(document.documentElement.lang || '');
          status.textContent = engels ? 'Enter your licence plate first.' : 'Vul eerst je kenteken in.';
          melding(true);
          inBeeld(status);
          input.focus({ preventScroll: true });
          return;
        }

        bezig = true;
        /* De vlag hierboven voorkomt een tweede opvraging al, maar zonder
           zichtbaar teken blijft iemand klikken. Elke klik telt mee voor de
           limiet en voor het fotoquotum. */
        if (knop) {
          knop.disabled = true;
          /* Uitgegrijsd zonder tekstverandering leest als "kapot" in plaats van
             "bezig". */
          knop.textContent = /^en/i.test(document.documentElement.lang || '') ? 'Searching…' : 'Zoeken…';
        }
        wagenWeg();
        opDeHoogteWeg();
        planWeg();
        status.textContent = T('We zoeken je auto op', 'Looking up your car');
        status.classList.add('on');
        melding(false);
        if (!reduced && plaat) plaat.classList.add('scan');
        skelet(true);

        window.DFData.lookupPlate(kt).then(function (v) {
          klaar();
          /* Meer dan een uitvoering onder hetzelfde kenteken. Het antwoord
             heeft dan gevonden:false, maar het is geen misser: de cijfers
             zitten er al in, alleen weet de dienst niet welke rij het is.
             Dus geen belverzoek maar een keuzelijst. Een tweede opvraging is
             niet nodig; elke rij draagt zijn eigen vermogen en koppel. */
          if (v && v.status === 'meerdere') {
            var rijen = bruikbareUitvoeringen(v);
            if (rijen.length) {
              var vanzelf = bouwjaarTreffer(rijen);
              if (vanzelf) neem(v, vanzelf, true);
              else kiesUitvoering(v);
              meld('meerdere');
              return;
            }
          }
          if (!v || v.gevonden === false) {
            /* Alles zonder een uitdrukkelijke gevonden:false telt als treffer,
               zodat een antwoord dat het veld weglaat niet stilletjes als
               mislukt wordt gelezen. */
            geenTreffer(v, input.value.trim());
            meld((v && typeof v.status === 'string' && v.status) || 'fout');
            return;
          }
          status.textContent = '';
          status.classList.remove('on');
          melding(false);
          vul(v);
          meld('zeker');
        }).catch(function (err) {
          klaar();
          /* Het skelet heeft de kaart zichtbaar gemaakt; zonder antwoord
             hoort hij weer dicht, anders staat er een leeg kader onder de
             melding. Zelfde opruiming als bij geenTreffer. */
          if (result) result.hidden = true;
          wagenWeg();
          status.textContent = foutTekst(err);
          melding(true);
          inBeeld(status);
          meld('fout');
        });
      });

      /* Binnengekomen met een kenteken in de link, bijvoorbeeld via de knop op
         de homepagina? Vul hem in en zoek meteen op. Alleen op een pagina die
         de uitkomst ook kan tonen, en een keer per pagina: sommige pagina's
         hebben meer dan een kentekenveld. formatPlate knipt af op zes tekens,
         dus wat er ook in de link staat, er gaat niets geks het veld in. */
      if (result && !window.__dfKtUitLink) {
        var uitLink = new URLSearchParams(location.search).get('kenteken');
        if (uitLink) {
          window.__dfKtUitLink = true;
          input.value = window.DFData.formatPlate(uitLink);
          form.dispatchEvent(new Event('submit', { cancelable: true }));
        }
      }

      /* Sitebreed onthouden: het veld voorinvullen, en waar een resultaatkaart
         staat de laatste uitkomst direct neerzetten - uit het geheugen, dus
         zonder opvraging en zonder scroll. Een kenteken in de link gaat voor:
         dat is een verse vraag. Eén kaart per pagina, zelfde patroon als de
         link-route hierboven. Bootmod3 heeft geen resultaatkaart en krijgt
         dus alleen het vooringevulde veld; de eigen flow daar blijft met
         rust. */
      var onthouden = ktLees();
      if (onthouden && !input.value) {
        input.value = window.DFData.formatPlate(onthouden.kt);
      }
      if (result && onthouden && onthouden.v && onthouden.v.vermogen && onthouden.v.koppel &&
          !window.__dfKtUitLink && !window.__dfKtUitCache) {
        window.__dfKtUitCache = true;
        var hadNoScroll = form.hasAttribute('data-no-scroll');
        form.setAttribute('data-no-scroll', '');
        vul(onthouden.v);
        /* vul() kijkt pas een frame later of hij mag scrollen, dus het merkje
           moet nog even blijven staan voordat het terug mag naar hoe de
           sectie hem had. */
        if (!hadNoScroll) {
          setTimeout(function () { form.removeAttribute('data-no-scroll'); }, 400);
        }
      }

      /* Zoekt de bezoeker elders op deze pagina een ander kenteken op - in
         het menupaneel bijvoorbeeld - dan hoort deze kaart niet bij een
         andere auto te blijven staan. Bij een volledig antwoord tekenen we
         hem opnieuw, anders gaat hij dicht. Onze eigen zoekactie slaan we
         over: die handelt zichzelf af, van skelet tot kaart. */
      document.addEventListener('df:kenteken', function (e) {
        if (!result || bezig) return;
        var d = (e && e.detail) ? e.detail : null;
        if (!d || !d.kt) return;
        /* Wat we zelf net getekend hebben komt via de datalaag als bericht
           terug; daar hoeven we niet nog een keer op te tekenen. */
        if (d.kt === getekendKt) return;
        var nu = result.querySelector('[data-kt="kenteken"]');
        if (nu && nu.textContent === d.kt) return;
        if (d.v && d.v.vermogen && d.v.koppel) {
          /* De melding, het blok "Hou mij op de hoogte" en de afspraakknop
             van een eerdere zoekactie hier horen bij een ander kenteken dan
             de kaart die nu komt (17-10-2026). */
          status.textContent = '';
          melding(false);
          opDeHoogteWeg();
          planWeg();
          var hadNoScroll2 = form.hasAttribute('data-no-scroll');
          form.setAttribute('data-no-scroll', '');
          vul(d.v);
          if (!hadNoScroll2) {
            setTimeout(function () { form.removeAttribute('data-no-scroll'); }, 400);
          }
        } else if (result.classList.contains('on')) {
          result.classList.remove('on');
          result.hidden = true;
        }
      });

      /* Alleen rijen waar we ook echt iets mee kunnen. vul() rekent met
         vermogen en koppel; ontbreekt een van beide, dan is die rij geen
         keuze maar een fout in de maak. */
      function bruikbareUitvoeringen(v) {
        if (!Array.isArray(v.uitvoeringen)) return [];
        return v.uitvoeringen.filter(function (u) {
          return u && u.vermogen && u.koppel &&
                 typeof u.vermogen.origineel === 'number' && typeof u.vermogen.stage1 === 'number' &&
                 typeof u.koppel.origineel === 'number' && typeof u.koppel.stage1 === 'number';
        });
      }

      /* Draagt precies een rij de vlag past_bij_bouwjaar, dan is dat degene die
         wij zelf ook zouden kiezen en passen we hem meteen toe. Dan hoeft de
         bezoeker niet te klikken voor een keuze die eigenlijk al vaststaat.

         Bij nul treffers geeft het bouwjaar geen uitsluitsel en is het een
         echte vraag. Bij meer dan een ook: dan weten we het nog steeds niet.
         In allebei die gevallen tonen we gewoon de lijst.

         Strikt op true vergelijken is hier expres. Komt er ooit een 1 of een
         "true" uit de dienst, dan valt hij door naar de keuzelijst - dat is
         het gedrag van voorheen en dus veilig. */
      function bouwjaarTreffer(rijen) {
        var raak = rijen.filter(function (u) { return u.past_bij_bouwjaar === true; });
        return raak.length === 1 ? raak[0] : null;
      }

      /* De keuzelijst. Alles met createElement en textContent: de namen komen
         van buiten en horen geen opmaak mee te kunnen brengen.

         De generatie staat er met opzet bij. Twee rijen kunnen exact dezelfde
         naam hebben - "3.0 TDI 204pk" voor zowel de MK1 als de MK2 - en dan
         kiest iemand tussen twee gelijke knoppen. De generatie en de winst
         maken het verschil zichtbaar. */
      function kiesUitvoering(v, kopTekst) {
        var rijen = bruikbareUitvoeringen(v);
        status.textContent = '';
        status.classList.remove('on');
        melding(false);
        /* Nog geen cijfers, dus nog geen kaart: het skelet gaat dicht tot er
           een uitvoering gekozen is. Anders stond de lijst boven een leeg
           kader, en op een telefoon was dat kader het enige in beeld. */
        if (result) result.hidden = true;

        var blok = document.createElement('div');
        blok.className = 'df-ktkies__blok';

        var kop = document.createElement('p');
        kop.className = 'df-ktkies__kop';
        /* Ook hier schrijft de dienst de tekst; die van ons is het vangnet. */
        var autoNaam = [v.merk, v.model, v.bouwjaar].filter(Boolean).join(' ');
        kop.textContent = EN
          ? (autoNaam ? autoNaam + ' comes in several versions. Which one is yours?' : MELDINGEN_EN.meerdere)
          : (kopTekst || v.fout || (autoNaam + ' kent meerdere uitvoeringen. Welke heb jij?'));
        blok.appendChild(kop);

        var lijst = document.createElement('div');
        lijst.className = 'df-ktkies';
        lijst.setAttribute('role', 'group');
        lijst.setAttribute('aria-label', T('Kies je uitvoering', 'Choose your version'));

        rijen.forEach(function (u) {
          var knop = document.createElement('button');
          knop.type = 'button';
          knop.className = 'df-ktkies__rij';

          var tekst = document.createElement('span');
          tekst.className = 'df-ktkies__tekst';

          var naam = document.createElement('b');
          naam.textContent = pkInTekst(u.naam) || T('Onbekende uitvoering', 'Unknown version');
          tekst.appendChild(naam);

          var onder = u.generatie || (u.jaar_vanaf ? T('vanaf ', 'from ') + u.jaar_vanaf : '');
          if (onder) {
            var sub = document.createElement('span');
            sub.textContent = onder;
            tekst.appendChild(sub);
          }
          knop.appendChild(tekst);

          var winst = document.createElement('i');
          winst.className = 'df-ktkies__winst';
          winst.textContent = u.vermogen.origineel + ' → ' + u.vermogen.stage1 + ' ' +
                              pkEenheid(u.vermogen.eenheid);
          knop.appendChild(winst);

          knop.addEventListener('click', function () { neem(v, u); });
          lijst.appendChild(knop);
        });

        blok.appendChild(lijst);
        status.appendChild(blok);
        inBeeld(status);
        if (rijen.length) lijst.firstChild.focus({ preventScroll: true });
        return blok;
      }

      /* Een gekozen rij maakt van het antwoord alsnog een treffer. De rij
         draagt vermogen en koppel in exact dezelfde vorm als bij een zeker
         antwoord, dus we zetten ze er overheen en laten vul() zijn werk doen.
         De id bewaren we bij het resultaat: daarmee is later vast te leggen
         wat de bezoeker koos. */
      function neem(v, u, opBouwjaar) {
        var g = {};
        Object.keys(v).forEach(function (k) { g[k] = v[k]; });
        delete g.uitvoeringen;
        /* De vlag van de tunekiezer (TUN-1) gaat niet mee de kaart en het
           geheugen in; vul() krijgt hem apart en beslist wat hij betekent. */
        var handmatig = !!v.handmatig;
        delete g.handmatig;
        g.gevonden = true;
        g.status = 'zeker';
        g.vermogen = u.vermogen;
        g.koppel = u.koppel;
        if (u.naam) g.motorisering = u.naam;
        if (u.generatie) g.generatie = u.generatie;
        if (u.id != null && result) result.dataset.uitvoering = u.id;

        status.textContent = '';
        melding(false);
        /* Een gekozen rij maakt de uitkomst van de zoekactie ervoor
           overbodig: "Hou mij op de hoogte" en de afspraakknop eronder
           hoorden bij "geen cijfers", en die zijn er nu wel. */
        opDeHoogteWeg();
        planWeg();
        var losVanKenteken = vul(g, handmatig);
        zetGekozenRegel(u, opBouwjaar, losVanKenteken);
      }

      /* Welke uitvoering het geworden is, staat naast de autonaam: de naam
         alleen zegt niet welke van de twee het is. Hier stond ook een knopje
         "andere uitvoering" dat de keuzelijst terughaalde; dat is eruit, want
         de bezoeker kiest een andere uitvoering nu gewoon in de lijsten boven
         de kaart. */
      function zetGekozenRegel(u, opBouwjaar, losVanKenteken) {
        if (!result) return;
        var naam = result.querySelector('[data-kt="auto"]');
        if (!naam || !naam.parentElement) return;

        var oud = result.querySelector('.df-ktkies__gekozen');
        if (oud) oud.parentElement.removeChild(oud);

        var delen = [];
        if (u.generatie) delen.push(u.generatie);
        /* Heeft de bezoeker zelf niets aangeklikt, dan hoort hij te zien dat
           wij die keuze hebben gemaakt en waarop. Anders staan er cijfers over
           zijn auto waar hij nooit ja tegen heeft gezegd. */
        if (opBouwjaar) delen.push(T('gekozen op bouwjaar', 'chosen by model year'));
        /* Zelf gekozen zonder dat het kenteken erbij hoort (TUN-1): dat moet
           de bezoeker zien, anders lijkt de kaart alsnog over het kenteken
           te gaan dat net nog in de plaat stond. */
        if (losVanKenteken) delen.push(T('Zelf gekozen, niet op kenteken', 'Chosen manually, not by licence plate'));
        if (!delen.length) return;

        var regel = document.createElement('span');
        regel.className = 'df-ktkies__gekozen';
        regel.textContent = delen.join(' · ');
        naam.parentElement.insertBefore(regel, naam.nextSibling);
      }

      /* Wat de bezoeker leest als er geen cijfers zijn. Alles wordt opgebouwd
         uit losse knopen: de tekst komt van buiten en mag geen opmaak mee de
         pagina in brengen. */
      function geenTreffer(v, kt) {
        var reden = v && v.status;
        /* Geen cijfers betekent: geen kaart. Het skelet heeft hem zichtbaar
           gemaakt om het wachten te vullen; blijft hij daarna staan, dan kijkt
           de bezoeker naar een leeg kader onder een melding. */
        if (result) result.hidden = true;
        wagenWeg();
        status.textContent = '';
        melding(true);
        status.appendChild(document.createTextNode(meldingTekst(v, reden)));

        /* Bij een limiet nodigen we niet uit om te bellen. Dat is geen klant
           met een vraag maar iemand die te snel achter elkaar klikt. */
        if (reden !== 'limiet') {
          contactregel(v, kt, reden);

          /* Een auto die we kennen maar waar nog geen cijfers voor zijn: dan
             kan de bezoeker zich laten bijpraten. Niet bij een onbekend
             kenteken, want daar valt niets af te wachten. En niet bij een
             volledig elektrische auto: daar zegt het platform zelf al dat er
             met chiptuning niets te winnen valt. */
          var elektrisch = v && String(v.brandstof || '').toLowerCase() === 'elektrisch';
          if (!elektrisch && (reden === 'kan_wel' || reden === 'niet_gevonden' || reden === 'geen_gegevens')) {
            opDeHoogte(v, kt, reden);
          }
          /* "Deze auto kunnen we tunen" had geen afspraakknop (TUN-4,
             17-10-2026): de enige knop was "Hou mij op de hoogte", terwijl
             de auto gewoon de werkplaats in kan. Bij kan_wel staat de
             afspraak daarom als hoofdroute boven dat blok, en wordt het blok
             zelf de tweede keus. Bij niet_gevonden en geen_gegevens blijft
             het zoals het was: daar is een afspraak nog geen zekere zaak. */
          if (!elektrisch && reden === 'kan_wel') planAfspraak(kt);
        }

        /* Pas nu meten, met de contactregel erbij: op een telefoon viel die
           regel anders net onder de rand. */
        inBeeld(status);
      }

      /* "Hou mij op de hoogte" (10-10-2026, op verzoek van Luuk). Het
         formulier staat als <template data-kt-opdehoogte> in de sectie
         (snippet df-kt-opdehoogte) en is een gewoon Shopify-contactformulier:
         wat de bezoeker invult komt als contactbericht in de winkelmail, met
         kenteken en auto erbij. Een sectie zonder dat sjabloon krijgt het
         niet.

         Het komt onder het kentekenformulier te staan en niet in de
         statusregel: die zit binnen dat formulier, en een formulier in een
         formulier zou bij versturen ook de kentekenzoekactie aansturen.

         Versturen gaat eerst op de achtergrond, zodat de bezoeker blijft
         waar hij is. Stuurt Shopify ons naar zijn spamcontrole (/challenge),
         dan alsnog op de gewone manier: dan ziet de bezoeker die controle en
         komt hij daarna terug op deze pagina met ?contact_posted=true. Daar
         vangen we hem op met de bedankregel; zie onderaan. */
      var ohSjabloon = sectie.querySelector('template[data-kt-opdehoogte]');
      var ohBlok = null;

      function opDeHoogteWeg() {
        if (ohBlok && ohBlok.parentNode) ohBlok.parentNode.removeChild(ohBlok);
        ohBlok = null;
      }

      function opDeHoogte(v, kt, reden) {
        if (!ohSjabloon || !ohSjabloon.content || !form.parentNode) return;
        opDeHoogteWeg();
        var frag = ohSjabloon.content.cloneNode(true);
        var blok = frag.querySelector('[data-kt-oh]');
        var f = blok ? blok.querySelector('form') : null;
        if (!f) return;

        var velden = {
          kenteken: window.DFData.formatPlate(kt || (v && v.kenteken) || ''),
          auto: v ? [v.merk, v.model, v.motorisering].filter(Boolean).join(' ') : '',
          bouwjaar: v && v.bouwjaar ? String(v.bouwjaar) : '',
          brandstof: (v && v.brandstof) || '',
          motorcode: (v && v.motorcode) || '',
          reden: reden || ''
        };
        Object.keys(velden).forEach(function (k) {
          var el = blok.querySelector('[data-kt-oh-veld="' + k + '"]');
          if (el) el.value = velden[k];
        });

        var ohKnop = f.querySelector('button[type="submit"]');
        var ohFout = blok.querySelector('[data-kt-oh-fout]');
        var ohKnopTekst = ohKnop ? ohKnop.textContent : '';

        function bedankt() {
          blok.textContent = '';
          var p = document.createElement('p');
          p.className = 'df-ktoh__dank';
          p.setAttribute('role', 'status');
          p.textContent = ohSjabloon.getAttribute('data-dank') || '';
          blok.appendChild(p);
        }

        function gewoonVersturen() {
          try { sessionStorage.setItem('df_kt_oh', '1'); } catch (er) {}
          HTMLFormElement.prototype.submit.call(f);
        }

        f.addEventListener('submit', function (e) {
          e.preventDefault();
          e.stopPropagation();
          if (!f.checkValidity()) { f.reportValidity(); return; }
          if (ohFout) ohFout.hidden = true;
          if (ohKnop) {
            ohKnop.disabled = true;
            ohKnop.textContent = ohSjabloon.getAttribute('data-bezig') || ohKnopTekst;
          }
          if (!window.fetch || !window.FormData) { gewoonVersturen(); return; }
          /* Shopify zet Cloudflare voor het contactformulier. Vertrouwt die het
             verzoek niet, dan komt er een 403 met cf-mitigated: challenge terug
             (gezien 10-10-2026 vanuit een testbrowser). Een verzoek op de
             achtergrond kan die controle niet doorlopen; dan dus alsnog op de
             gewone manier, zodat de bezoeker de controle ziet en daarna
             terugkomt. Alleen een gewoon antwoord zonder contact_posted is een
             echte fout. Gaat het verzoek zelf mis, dan ook op de gewone
             manier: liever een paginawissel dan een verloren aanmelding. */
          fetch(f.action, { method: 'POST', body: new FormData(f), credentials: 'same-origin' })
            .then(function (r) {
              var waar = r.url || '';
              if (waar.indexOf('contact_posted=true') > -1) { bedankt(); return; }
              if (r.status === 403 || r.status === 429 || r.headers.get('cf-mitigated') ||
                  waar.indexOf('/challenge') > -1) {
                gewoonVersturen();
                return;
              }
              if (ohKnop) { ohKnop.disabled = false; ohKnop.textContent = ohKnopTekst; }
              if (ohFout) ohFout.hidden = false;
            })
            .catch(function () { gewoonVersturen(); });
        });

        form.parentNode.insertBefore(frag, form.nextSibling);
        ohBlok = blok;
      }

      /* De afspraakknop bij kan_wel (TUN-4). Komt tussen het kentekenformulier
         en het blok "Hou mij op de hoogte" te staan, dus na dat blok
         aanroepen. De link draagt dienst en kenteken: de afspraakpagina
         (df-afspraakflow) vinkt chiptuning dan alvast aan en vult de plaat
         in. Met het taalvoorvoegsel, net als de knop naar de onderdelen. De
         opmaak van .df-ktplan staat in snippets/df-kt-opdehoogte.liquid. */
      var planBlok = null;

      function planWeg() {
        if (planBlok && planBlok.parentNode) planBlok.parentNode.removeChild(planBlok);
        planBlok = null;
      }

      function planAfspraak(kt) {
        if (!form.parentNode) return;
        planWeg();
        var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        var adres = root + 'pages/afspraak?dienst=chiptuning';
        var net = window.DFData.formatPlate(kt || '');
        if (net) adres += '&kenteken=' + encodeURIComponent(net);

        var blok = document.createElement('div');
        blok.className = 'df-ktplan';
        var knop = document.createElement('a');
        knop.className = 'btn btn-red df-ktplan__knop';
        knop.href = adres;
        knop.textContent = T('Plan een tuningafspraak', 'Book a tuning appointment');
        blok.appendChild(knop);
        form.parentNode.insertBefore(blok, form.nextSibling);
        planBlok = blok;

        /* Het blok eronder wordt de tweede keus: omlijnd in plaats van rood,
           zodat er niet twee rode knoppen onder elkaar staan. */
        if (ohBlok) {
          var ohKnop = ohBlok.querySelector('.df-ktoh__knop');
          if (ohKnop) {
            ohKnop.classList.remove('btn-red');
            ohKnop.classList.add('btn-ghost');
          }
        }
      }

      /* Terug van de spamcontrole van Shopify: het bericht is verstuurd, dus
         de bedankregel in de statusregel. Alleen als dit formulier het
         gewone versturen zelf heeft aangezet; een contact_posted van het
         contactformulier op een andere pagina gaat ons niet aan. */
      if (ohSjabloon && /[?&]contact_posted=true/.test(location.search)) {
        var ohVlag = null;
        try {
          ohVlag = sessionStorage.getItem('df_kt_oh');
          sessionStorage.removeItem('df_kt_oh');
        } catch (er) {}
        if (ohVlag) {
          status.textContent = ohSjabloon.getAttribute('data-dank') || '';
          melding(true);
        }
      }

      /* WhatsApp en telefoon als echte links onder de melding. De tekst
         schrijft de dienst, klikbaar maken is aan het thema.

         Bewust op een eigen regel en zonder verbindende woorden: de teksten
         van de dienst eindigen vaak al met "bel of app ons gerust". Zetten we
         daar een zin achteraan, dan staat het er twee keer. Zo leest het als
         de contactgegevens die bij die zin horen. */
      function contactregel(v, kt, reden) {
        var auto = v ? [v.merk, v.model, v.motorisering].filter(Boolean).join(' ') : '';
        var wat = kt || T('auto', 'car');
        if (auto) wat += ' (' + auto + ')';
        /* Bij kan_wel weten we wat de vraag is en vullen we hem voor. In de
           andere gevallen niet: bij een elektrische auto zou "wat levert Stage
           1 op" een onzinvraag zijn om iemand in de mond te leggen. */
        var bericht = EN
          ? (reden === 'kan_wel'
            ? 'Hi, what does Stage 1 do for my ' + wat + '?'
            : 'Hi, I have a question about my ' + wat + '.')
          : (reden === 'kan_wel'
            ? 'Hoi, wat levert Stage 1 op voor mijn ' + wat + '?'
            : 'Hoi, ik heb een vraag over mijn ' + wat + '.');

        var regel = document.createElement('span');
        regel.className = 'df-ktstat__acties';

        var wa = document.createElement('a');
        wa.href = 'https://wa.me/31525796515?text=' + encodeURIComponent(bericht);
        wa.target = '_blank';
        wa.rel = 'noopener';
        wa.textContent = T('App ons', 'WhatsApp us');
        regel.appendChild(wa);

        regel.appendChild(document.createTextNode(' · '));

        var tel = document.createElement('a');
        tel.href = 'tel:0525796515';
        tel.textContent = '0525-796515';
        regel.appendChild(tel);

        status.appendChild(regel);
      }

      /* Kader om de statusregel, maar alleen bij een echte uitkomst.
         Tijdens het zoeken en bij de gewone hint hoort hij plat te blijven. */
      function melding(aan) {
        if (status) status.classList.toggle('df-melding', !!aan);
      }

      function klaar() {
        bezig = false;
        if (knop) { knop.disabled = false; knop.textContent = knopTekst; }
        skelet(false);
        status.classList.remove('on');
        if (plaat) plaat.classList.remove('scan');
      }

      function vul(v, handmatig) {
        if (!result) return false;
        /* Van een vorige zoekactie kan er nog een keuzeregel in de kaart
           staan; die hoort niet bij deze auto. */
        var vorige = result.querySelector('.df-ktkies__gekozen');
        if (vorige) vorige.parentElement.removeChild(vorige);
        var pkPlus = v.vermogen.stage1 - v.vermogen.origineel;
        var pk = ' ' + pkEenheid();
        var nmPlus = v.koppel.stage1 - v.koppel.origineel;
        /* De API geeft het kenteken zonder streepjes terug (RJ066B). Op het
           scherm en in de doorklik-link willen we de leesbare vorm. */
        var ktNet = window.DFData.formatPlate(v.kenteken || (input && input.value) || '');
        /* Handmatig gekozen (TUN-1, 17-10-2026). De tunekiezer kent geen
           kenteken, maar in de plaat staat vaak nog het kenteken van een
           eerdere zoekactie, en dat nam de kaart over: de keuze werd onder
           dat kenteken onthouden, de links kregen het mee en de autokiezer
           in het menu zette er een auto bij die niet bij dat kenteken hoort.
           Nu hangt de keuze alleen aan het kenteken als het onthouden
           antwoord dezelfde auto noemt (merk en model gelijk: een andere
           uitvoering van dezelfde Golf) of als de opvraging niets opleverde
           (geen antwoord, dus ook geen auto om mee te botsen). Anders gaat
           de plaat leeg, blijft het kenteken uit de links en uit het
           geheugen, en zegt de kaart erbij dat hij zelf gekozen is. */
        var losVanKenteken = false;
        if (handmatig) {
          var ktVeld = window.DFData.formatPlate(input ? input.value : '');
          var oud = ktVeld ? ktLees() : null;
          var bekend = (oud && oud.kt === ktVeld) ? (oud.v || oud.auto || null) : null;
          var past = !!(oud && oud.kt === ktVeld && (!bekend || zelfdeAuto(bekend, v)));
          if (past) {
            ktNet = ktVeld;
            /* Zelfde auto als het kenteken: wat de opvraging over die auto
               wist en de keuze zelf niet meebrengt (bouwjaar, bak, foto)
               blijft gelden, ook in het geheugen en de autokiezer. */
            if (bekend) {
              ['bouwjaar', 'brandstof', 'transmissie', 'motorcode', 'afbeelding', 'afbeeldingen'].forEach(function (k) {
                if (v[k] == null && bekend[k] != null) v[k] = bekend[k];
              });
            }
          } else {
            ktNet = '';
            losVanKenteken = true;
            if (input) {
              input.value = '';
              input.removeAttribute('data-df-voorin');
            }
          }
        }
        getekendKt = ktNet;
        /* Dit is het ene succespad, ook na een uitvoeringkeuze en bij een
           kenteken uit de link - dus hier onthouden we kenteken en antwoord
           voor de rest van de site. Zonder kenteken valt er niets te
           onthouden: het geheugen houdt dan de auto van het kenteken. */
        if (ktNet) ktBewaar(ktNet, v);
        /* De chassiscode vooraan in motorisering ("G05 - 4.4 Bi-Turbo ...")
           komt uit het platform en klopt niet altijd: een X5 M is een F95,
           maar het platform zet er G05 voor. In de kop van de kaart laten we
           die code daarom weg; de motor en het vermogen erachter kloppen wel.
           Alleen voor de weergave, het antwoord zelf blijft heel. */
        var motor = String(v.motorisering || '')
          .replace(/^[A-Za-z]{0,2}\d{1,3}(?:\.\d)?[A-Za-z]{0,2}(?:[- ]?(?:LCI|FL))?\s*[-\u2013:]\s+/, '');
        var data = {
          'kenteken': ktNet,
          'auto': [v.merk, v.model, pkInTekst(motor)].filter(Boolean).join(' '),
          'merk': v.merk,
          'bouwjaar': v.bouwjaar || '',
          'brandstof': v.brandstof || '',
          'pk-van': v.vermogen.origineel + pk,
          'pk-naar': v.vermogen.stage1 + pk,
          'pk-plus': '+' + pkPlus + pk,
          'pk-pct': '+' + Math.round(pkPlus / v.vermogen.origineel * 100) + '%',
          'nm-van': v.koppel.origineel + ' Nm',
          'nm-naar': v.koppel.stage1 + ' Nm',
          'nm-plus': '+' + nmPlus + ' Nm',
          'nm-pct': '+' + Math.round(nmPlus / v.koppel.origineel * 100) + '%'
        };
        Object.keys(data).forEach(function (k) {
          result.querySelectorAll('[data-kt="' + k + '"]').forEach(function (el) {
            el.textContent = data[k];
          });
        });

        /* De vanaf-prijs (10-10-2026). Bij een automaat gaat de software van
           de versnellingsbak mee en dat kost meer: 349 tegen 549. Het platform
           kent de bak uit de typegoedkeuring van de RDW ("Handgeschakeld" of
           "Automaat"). Staat hij in het antwoord als transmissie, dan noemen
           we alleen het bedrag dat bij deze auto hoort. Zonder dat veld blijft
           staan wat de sectie neerzet: beide bedragen. Teksten en bedragen
           komen uit de data-attributen van het prijsblok, dus in de taal van
           de pagina en zoals ze in de thema-editor staan. */
        var bak = String(v.transmissie || '').toLowerCase();
        var soort = bak.indexOf('automa') > -1 ? 'auto' : (bak.indexOf('hand') > -1 ? 'hand' : 'beide');
        result.querySelectorAll('[data-kt-prijs]').forEach(function (pr) {
          var s = (soort === 'auto' && !pr.getAttribute('data-auto')) ? 'beide' : soort;
          var l = pr.querySelector('[data-kt-prijs-l]');
          var b = pr.querySelector('[data-kt-prijs-b]');
          var a = pr.querySelector('[data-kt-prijs-a]');
          if (l) l.textContent = pr.getAttribute(s === 'auto' ? 'data-l-auto' : 'data-l-vanaf') || '';
          if (b) b.textContent = pr.getAttribute(s === 'auto' ? 'data-auto' : 'data-hand') || '';
          if (a) a.textContent = pr.getAttribute('data-s-' + s) || '';
        });

        var balken = {
          pk: Math.round(v.vermogen.origineel / v.vermogen.stage1 * 100),
          nm: Math.round(v.koppel.origineel / v.koppel.stage1 * 100)
        };

        /* Voertuigfoto uit het antwoord. Het is een adres, geen bestand: de
           browser haalt hem bij het CDN van de leverancier op. De srcset en
           sizes van de themafoto moeten weg, anders wint die van de nieuwe src.
           Het slot staat buiten de resultaatkaart, dus zoeken op sectieniveau. */
        var wagen = sectie.querySelector('[data-kt-wagen]');
        var wagenblok = wagen && (wagen.closest('.df-kh__wagenblok') || wagen);
        /* Het bijschrift staat rechtsonder in de kaart, los van het fotoblok,
           maar hoort er wel bij: zonder foto slaat "Voorbeeldfoto van dit
           model" nergens op. Daarom gaan ze samen aan en uit. */
        var bijschrift = sectie.querySelector('[data-kt-bijschrift]');
        function toonWagen(aan) {
          if (wagenblok) wagenblok.hidden = !aan;
          if (bijschrift) bijschrift.hidden = !aan;
        }
        /* Geen foto bij dit antwoord? Dan ook geen foto van de vorige auto
           laten staan. */
        if (!v.afbeelding) toonWagen(false);
        if (wagen && v.afbeelding) {
          wagen.removeAttribute('srcset');
          wagen.removeAttribute('sizes');
          /* Niet meer uitstellen: de bezoeker heeft er net om gevraagd en het
             beeld staat in zicht. Met lazy wacht de browser op een
             zichtbaarheidscontrole en dat is hier alleen maar vertraging. */
          wagen.loading = 'eager';
          /* Het adres van hun CDN is ondertekend en houdbaar tot een bepaald
             moment. Valt hij weg, dan liever niets dan een kapot beeldje over
             de fotokop heen. */
          /* hidden in plaats van een klasse: het blok staat al verborgen in de
             HTML, zodat de bezoeker tijdens het laden geen voorbeeldfoto ziet
             voordat de stylesheet er is. Pas als deze foto binnen is mag het
             blok zichtbaar worden, en valt hij weg dan gaat hij weer dicht. */
          wagen.onerror = function () { toonWagen(false); };
          wagen.onload = function () { toonWagen(true); };
          wagen.alt = [v.merk, v.model, motor].filter(Boolean).join(' ');
          if (wagen.src === v.afbeelding && wagen.complete && wagen.naturalWidth) {
            /* Zelfde foto als net: het load-moment komt dan niet nog een keer,
               dus zelf openzetten. */
            toonWagen(true);
          } else {
            /* Eerst dicht. Anders staat bij een tweede kenteken de auto van de
               vorige zoekactie nog naast de nieuwe cijfers. */
            toonWagen(false);
            wagen.src = v.afbeelding;
          }
        }

        /* Vanaf deze kaart klikt de bezoeker door, bijvoorbeeld van de
           homepagina naar chiptuning. Neem het kenteken mee in die links,
           anders moet hij het daar opnieuw intikken. Alleen links binnen de
           eigen site, en netjes via URL zodat een bestaande ?dienst= blijft
           staan. */
        var ktLink = ktNet;
        result.querySelectorAll('a[href]').forEach(function (a) {
          var u;
          try { u = new URL(a.getAttribute('href'), location.origin); }
          catch (err) { return; }
          if (u.origin !== location.origin) return;
          if (ktLink) {
            u.searchParams.set('kenteken', ktLink);
          } else {
            /* Zonder kenteken (zelf gekozen) gaat het kenteken van de vorige
               kaart er juist uit, anders reist dat oude kenteken alsnog mee
               naar de chiptuningpagina en rekent die met de verkeerde auto. */
            if (!u.searchParams.has('kenteken')) return;
            u.searchParams.delete('kenteken');
          }
          a.setAttribute('href', u.pathname + u.search + u.hash);
        });

        /* De knop naar de onderdelen rechtstreeks naar de collectie van deze
           auto; zie onderdelenPad bovenin. Het kenteken blijft erachter
           staan: de kop van de shop zoekt er dan vanzelf op (df-auto.js). */
        result.querySelectorAll('[data-kt-onderdelen]').forEach(function (a) {
          var pad = onderdelenPad(v);
          if (!pad) return;
          a.setAttribute('href', ktLink ? pad + '?kenteken=' + encodeURIComponent(ktLink) : pad);
        });

        result.hidden = false;
        /* Twee delen in de balk: het grijze origineel staat er meteen, het
           rode stuk begint waar het grijs ophoudt en groeit tot de rand. Een
           kaart met alleen [data-kt-bar] (zonder basis) werkt nog net zo:
           dan begint het rood gewoon op nul. */
        result.querySelectorAll('[data-kt-basis]').forEach(function (b) {
          b.style.width = balken[b.dataset.ktBasis] + '%';
        });
        result.querySelectorAll('[data-kt-bar]').forEach(function (b) {
          var basis = b.parentElement && b.parentElement.querySelector('[data-kt-basis]');
          b.style.left = basis ? balken[b.dataset.ktBar] + '%' : '0%';
          b.style.width = '0%';
        });

        requestAnimationFrame(function () {
          result.classList.add('on');
          var zet = function () {
            result.querySelectorAll('[data-kt-bar]').forEach(function (b) {
              var basis = b.parentElement && b.parentElement.querySelector('[data-kt-basis]');
              b.style.width = (basis ? 100 - balken[b.dataset.ktBar] : balken[b.dataset.ktBar]) + '%';
            });
          };
          if (reduced) { zet(); }
          else { setTimeout(zet, 150); }
          if (!form.hasAttribute('data-no-scroll')) {
            /* Alleen als de kaart niet helemaal in beeld staat. Op een
               telefoon begint hij onder de vouw (op de homepagina op 686px
               bij een scherm van 664px, dus de bezoeker zag na het zoeken
               niets gebeuren); op een breed scherm staat hij na een tweede
               kenteken vaak al in beeld en dan hoeft er niets te schuiven.
               Een kaart die hoger is dan het scherm komt met zijn kop
               bovenin, een kleinere in het midden. */
            var kader = result.getBoundingClientRect();
            var hoog = window.innerHeight;
            if (kader.top < 0 || kader.bottom > hoog) {
              result.scrollIntoView({
                behavior: reduced ? 'auto' : 'smooth',
                block: kader.height > hoog * 0.9 ? 'start' : 'center'
              });
            }
          }
        });
        return losVanKenteken;
      }
    });
  }

  init(document);
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
